import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";

// Module-level cache to store role_id for a user
// Structure: { [userId: string]: { roleId: number, timestamp: number } }
const roleCache: Record<string, { roleId: number; timestamp: number }> = {};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export function useAdminAccess() {
  const [hasAccess, setHasAccess] = useState(false);
  const [hasOwnerAccess, setHasOwnerAccess] = useState(false);
  const [hasTeamLeadAccess, setHasTeamLeadAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    let mounted = true;

    async function checkAccess() {
      try {
        if (!user) {
          if (mounted) {
            setHasAccess(false);
            setHasOwnerAccess(false);
            setHasTeamLeadAccess(false);
            setLoading(false);
          }
          return;
        }

        // Check cache first
        const cached = roleCache[user.id];
        const now = Date.now();

        if (cached && now - cached.timestamp < CACHE_DURATION) {
          // Use cached value
          if (mounted) {
            setHasAccess(cached.roleId === 1 || cached.roleId === 2);
            setHasOwnerAccess(cached.roleId === 1);
            setHasTeamLeadAccess(cached.roleId === 3);
            setLoading(false);
          }
          return;
        }

        // If no cache or expired, fetch from Supabase
        const { data: workerData, error } = await supabase
          .from("workers")
          .select("role_id")
          .eq("user_id", user.id)
          .single();

        if (error) {
          console.error("Error checking admin access:", error);
          if (mounted) {
            setHasAccess(false);
            setHasOwnerAccess(false);
            setHasTeamLeadAccess(false);
          }
          return;
        }

        // Update cache
        if (workerData) {
          roleCache[user.id] = {
            roleId: workerData.role_id,
            timestamp: now,
          };

          if (mounted) {
            setHasAccess(workerData.role_id === 1 || workerData.role_id === 2);
            setHasOwnerAccess(workerData.role_id === 1);
            setHasTeamLeadAccess(workerData.role_id === 3);
          }
        }
      } catch (err) {
        console.error("Error checking admin access:", err);
        if (mounted) {
          setHasAccess(false);
          setHasOwnerAccess(false);
          setHasTeamLeadAccess(false);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    checkAccess();

    return () => {
      mounted = false;
    };
  }, [user]);

  return { hasAccess, hasOwnerAccess, hasTeamLeadAccess, loading, roleId: roleCache[user?.id || '']?.roleId ?? null };
}
